import { ADMIN_PRINT_STYLE_ORDER } from '@/mocks/adminCatalog';
import type { IAdminReference, TAdminPlacementKey } from '@/mocks/adminCatalog';
import type { EPrintStyle } from '@/typescript/enums/EPrintStyle';

// Back-office mock-up: a design is offered per variant, a reference × a print style × a position
// of the short band, the way the developers built it on main (template_product_variant, kept by
// Katia on 2026-10-05). A variant takes the design only if it has every zone the design fills.

interface IAdminTemplateVariant {
    code: string;
    placement: TAdminPlacementKey | null;
    style: EPrintStyle;
}

const KEY_SEPARATOR = '|';

function toVariantKey(variant: IAdminTemplateVariant): string {
    return [variant.code, variant.style, variant.placement ?? ''].join(KEY_SEPARATOR);
}

function fromVariantKey(key: string): IAdminTemplateVariant {
    const [code, style, placement] = key.split(KEY_SEPARATOR);
    return { code, style: style as EPrintStyle, placement: (placement || null) as TAdminPlacementKey | null };
}

// Every variant a reference offers: its active styles, one per position when the band moves.
function referenceVariants(reference: IAdminReference): IAdminTemplateVariant[] {
    return ADMIN_PRINT_STYLE_ORDER.flatMap((style) => {
        const printStyle = reference.printStyles.find((candidate) => candidate.style === style);
        if (!printStyle?.isActive) {
            return [];
        }
        return printStyle.marginVariants.map((variant) => ({ code: reference.code, style, placement: variant.placement }));
    });
}

function missingZoneKeys(zoneKeys: string[], reference: IAdminReference, style: EPrintStyle): string[] {
    const offered = reference.printStyles.find((candidate) => candidate.style === style)?.zones.map((zone) => zone.key) ?? [];
    return zoneKeys.filter((key) => !offered.includes(key));
}

// A design drawn on one zone goes on any variant, the pitchers' faces included (Katia, 2026-10-06:
// "les pichets peuvent fonctionner aussi"); one drawn on two faces needs both. Damien's rule on
// main refuses a one-zone design on the pitchers: to raise with him.
function fitsTemplate(zoneKeys: string[], reference: IAdminReference, style: EPrintStyle): boolean {
    return zoneKeys.length <= 1 || missingZoneKeys(zoneKeys, reference, style).length === 0;
}

function offeredReferenceCodes(variantKeys: string[]): string[] {
    return [...new Set(variantKeys.map((key) => fromVariantKey(key).code))];
}

function offeredPrintStyles(variantKeys: string[]): EPrintStyle[] {
    const styles = new Set(variantKeys.map((key) => fromVariantKey(key).style));
    return ADMIN_PRINT_STYLE_ORDER.filter((style) => styles.has(style));
}

export { fitsTemplate, fromVariantKey, offeredPrintStyles, offeredReferenceCodes, referenceVariants, toVariantKey };

export type { IAdminTemplateVariant };
